import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { useSiteContent } from '../context/SiteContentContext';
import {
  Phone,
  Users,
  Network,
  Shield,
  Cloud,
  LineChart,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

const iconMap = {
  Users: Users,
  Network: Network,
  Shield: Shield,
  Cloud: Cloud,
  LineChart: LineChart
};

const ServicosPage = () => {
  const { siteContent } = useSiteContent();
  const companyInfo = siteContent.companyInfo;
  const services = siteContent.services;
  const images = siteContent.images;
  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent(companyInfo.whatsappMessage)}`,
      '_blank'
    );
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute inset-0 opacity-10">
          <img src={images.cloud} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute bottom-1/4 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
              Nossos Serviços
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Soluções completas em <span className="text-orange-500">TI</span>
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Oferecemos uma gama completa de serviços de TI para manter sua empresa 
              operando com segurança, eficiência e sem interrupções.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className="inline-flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                        {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                      </div>
                      <span className="text-sm font-medium text-orange-500 uppercase tracking-wider">
                        Serviço {index + 1}
                      </span>
                    </div>
                    
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                      {service.title}
                    </h2>
                    
                    <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="grid sm:grid-cols-2 gap-4 mb-8">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      onClick={handleWhatsApp}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 shadow-lg shadow-orange-500/25"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Solicitar orçamento
                    </Button>
                  </div>

                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className="relative">
                      <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-blue-500/20 rounded-3xl blur-2xl" />
                      <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                        <img
                          src={service.image || (index === 0 ? images.services : index === 1 ? images.network : index === 2 ? images.security : index === 3 ? images.cloud : images.about)}
                          alt={service.title}
                          className="w-full h-80 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-medium mb-4">
              Como Trabalhamos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Nosso processo de atendimento
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Um processo estruturado para garantir a melhor solução para sua empresa
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Diagnóstico', description: 'Analisamos seu ambiente atual e identificamos oportunidades de melhoria.' },
              { step: '02', title: 'Proposta', description: 'Apresentamos soluções personalizadas para suas necessidades específicas.' },
              { step: '03', title: 'Implementação', description: 'Executamos o projeto com mínimo impacto na sua operação.' },
              { step: '04', title: 'Suporte', description: 'Acompanhamento contínuo e suporte especializado.' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-4 text-white font-bold text-xl">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <ArrowRight className="w-6 h-6 text-slate-300 -ml-3" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Precisa de uma solução personalizada?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Entre em contato e nossa equipe irá analisar as necessidades da sua empresa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsApp}
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-lg shadow-xl shadow-orange-500/25"
            >
              <Phone className="w-5 h-5 mr-2" />
              Fale com um especialista
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
            >
              <Link to="/contato">Ver formas de contato</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicosPage;
